import { ThemeProvider } from '@mui/material/styles'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { observer } from 'mobx-react'
import { SnackbarProvider } from 'notistack'
import React, { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'
import { CircularProgress, Box } from '@mui/material'
import { STRIPE_KEY } from './api-config'
import './App.css'
import { Strings, StringsContext } from './assets/localization/strings'
import Auth, { AuthPage } from './components/auth/Auth'
import { MockDialog, MockStoreContext, useMock } from './components/common/MockView'
import TodoPopup from './components/common/TodoPopup'
import UTMHandler from './components/common/UTMHandler'
import { mock } from './reusableUtils/Helpers'
import BrandRoute from './route/BrandRoute'
import PrivateRoute from './route/PrivateRoute'
import PublicRoute from './route/PublicRoute'
import { theme } from './theme'

// IMPROVEMENT 5: Lazy load route components to reduce initial bundle size (50-70% reduction expected)
const CampaignDashboard = lazy(() => import('./components/dashboard/CampaignDashboard'))
const ContentCampaignOverview = lazy(() => import('./components/contentCampaignOverview/ContentCampaignOverview'))
const CreatorProfile = lazy(() => import('./components/contentCampaignOverview/CreatorProfile'))
const UGCCampaignForm = lazy(() => import('./components/createUgcCampaign/UGCCampaignForm'))
const BrandForm = lazy(() => import('./components/createBrand/BrandForm'))
const BrandSettingsWrap = lazy(() => import('./components/brandSettings/BrandSettingsWrap'))
const Settings = lazy(() => import('./components/settings/Settings'))
const NewPassword = lazy(() => import('./components/settings/NewPassword'))
const NotFound = lazy(() => import('./components/error/NotFound'))

const stripePromise = loadStripe(STRIPE_KEY)

export const App = observer(() => {
  const mockObj = useMock()
  const [language, setLanguage] = useState(window.navigator.language.includes('en') ? 'en' : 'de')
  const strings = useMemo(() => Strings(language), [language])
  mock({
    english: () => setLanguage('en'),
    german: () => setLanguage('de')
  })
  useEffect(() => {
    console.log(language)
  }, [language])

  return (
    <StringsContext.Provider value={{ strings, language, setLanguage }}>
      <MockStoreContext.Provider value={mockObj.value}>
        <ThemeProvider theme={theme}>
          <MockDialog {...mockObj} />
          <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
            <UTMHandler>
              <div className='App'>
                <div>
                  {/* Spacing für Header und Sidebar */}
                  <Elements
                    stripe={stripePromise}
                    options={{
                      locale: language === 'de' ? 'de' : 'en'
                    }}>
                    <Suspense
                      fallback={
                        <Box display='flex' justifyContent='center' alignItems='center' minHeight='100vh'>
                          <CircularProgress />
                        </Box>
                      }>
                      <Switch>
                        <BrandRoute exact path='/:brandId/settings/:site' component={BrandSettingsWrap} />
                        {/* "/dashboard" */}
                        <BrandRoute path='/:brandId/dashboard' exact component={CampaignDashboard} />
                        {/* "/CampaignOverview" */}
                        <BrandRoute path='/:brandId/createContentCampaign/:couponCode?' exact component={UGCCampaignForm} showSidebar={false} isCollapsible={true} />
                        <BrandRoute path='/content/:brandId/:campaignId/:step?' exact component={ContentCampaignOverview} />
                        <BrandRoute path='/creator-profile/:influencerId' exact component={CreatorProfile} />
                        <BrandRoute
                          exact
                          isCollapsible
                          showSidebar={false}
                          path='/form/:brandId/:campaignId/:type' // should be view, update or duplicate
                          component={UGCCampaignForm}
                        />
                        <BrandRoute exact path='/createBrand' component={BrandForm} showSidebar={false} isCollapsible={true} />
                        <PrivateRoute exact path='/settings' component={Settings} isCollapsible={true} />
                        {/* not tracked */}
                        <PrivateRoute exact path='/newPassword' component={NewPassword} />
                        <PublicRoute exact path='/signup' component={() => <Auth page={AuthPage.signup} />} />
                        <PublicRoute exact path='/login' component={() => <Auth page={AuthPage.login} />} />
                        <PublicRoute exact path='/forgotPassword' component={() => <Auth page={AuthPage.forgotPassword} />} />
                        <PrivateRoute exact path='/onboarding' component={() => <Auth page={AuthPage.onboarding} />} />
                        <PrivateRoute exact path='/platformMode' component={() => <Auth page={AuthPage.platformMode} />} />

                        {/* Redirect /dashboard to / for proper brand handling */}
                        <Route exact path='/dashboard' render={() => <Redirect to='/' />} />

                        <BrandRoute exact path='/' component={CampaignDashboard} />

                        {/* 404 - Must be last route */}
                        <Route component={NotFound} />
                      </Switch>
                    </Suspense>
                  </Elements>
                </div>
                <TodoPopup />
              </div>
            </UTMHandler>
          </SnackbarProvider>
        </ThemeProvider>
      </MockStoreContext.Provider>
    </StringsContext.Provider>
  )
})
